import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Slide,
  Card,
  CardContent,
  useMediaQuery,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AutoAwesome as AIIcon,
  Psychology as BrainIcon,
  DragIndicator as DragIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as MinimizeIcon,
  ViewSidebar as SidebarIcon,
  ViewColumn as BottomIcon,
  Link as LinkIcon,
  Timeline as TimelineIcon,
  Shuffle as ShuffleIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';

interface MatchingExerciseModalProps {
  open: boolean;
  onClose: () => void;
  documentContent: string;
}

interface MatchingPair {
  id: string;
  leftItem: {
    id: string;
    text: string;
    position: { x: number; y: number };
  };
  rightItem: {
    id: string;
    text: string;
    position: { x: number; y: number };
  };
  isMatched: boolean;
  isCorrect: boolean | null;
  connectionId: string | null;
}

interface MatchingExercise {
  id: string;
  title: string;
  description: string;
  leftItems: Array<{
    id: string;
    text: string;
    position: { x: number; y: number };
  }>;
  rightItems: Array<{
    id: string;
    text: string;
    position: { x: number; y: number };
  }>;
  correctPairs: Array<{
    leftId: string;
    rightId: string;
  }>;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
}

const MatchingExerciseModal: React.FC<MatchingExerciseModalProps> = ({
  open,
  onClose,
  documentContent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [currentExercise, setCurrentExercise] = useState<MatchingExercise | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [completedExercises, setCompletedExercises] = useState<MatchingExercise[]>([]);
  const [isMaximized, setIsMaximized] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [connections, setConnections] = useState<Array<{
    id: string;
    from: string;
    to: string;
    isCorrect: boolean | null;
  }>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const [containerDimensions, setContainerDimensions] = useState<{ width: number; height: number } | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track container dimensions for responsive curve calculations
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerDimensions({ width: rect.width, height: rect.height });
        setForceUpdate(prev => prev + 1); // Force re-render of SVG paths
      }
    };

    updateDimensions();
    
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize events for immediate updates
    let resizeTimeout: NodeJS.Timeout;
    const handleWindowResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateDimensions();
      }, 16); // ~60fps
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(resizeTimeout);
    };
  }, [currentExercise]);

  // Sample matching exercises
  const sampleExercises: MatchingExercise[] = [
    {
      id: 'sample-1',
      title: 'Professional Skills Matching',
      description: 'Match the skills with their corresponding job roles',
      leftItems: [
        { id: 'left-1', text: 'Strategic Planning', position: { x: 0, y: 0 } },
        { id: 'left-2', text: 'Team Leadership', position: { x: 0, y: 0 } },
        { id: 'left-3', text: 'Budget Management', position: { x: 0, y: 0 } },
        { id: 'left-4', text: 'Brand Consistency', position: { x: 0, y: 0 } },
      ],
      rightItems: [
        { id: 'right-1', text: 'Marketing Manager', position: { x: 0, y: 0 } },
        { id: 'right-2', text: 'Project Manager', position: { x: 0, y: 0 } },
        { id: 'right-3', text: 'Financial Analyst', position: { x: 0, y: 0 } },
        { id: 'right-4', text: 'Brand Manager', position: { x: 0, y: 0 } },
      ],
      correctPairs: [
        { leftId: 'left-1', rightId: 'right-1' },
        { leftId: 'left-2', rightId: 'right-2' },
        { leftId: 'left-3', rightId: 'right-3' },
        { leftId: 'left-4', rightId: 'right-4' },
      ],
      difficulty: 'medium',
      category: 'Professional Skills',
    },
    {
      id: 'sample-2',
      title: 'Education & Experience Matching',
      description: 'Connect educational qualifications with relevant work experience',
      leftItems: [
        { id: 'left-1', text: 'Master of Business Management', position: { x: 0, y: 0 } },
        { id: 'left-2', text: 'Bachelor of Business', position: { x: 0, y: 0 } },
        { id: 'left-3', text: '3.8 GPA', position: { x: 0, y: 0 } },
        { id: 'left-4', text: 'Multilingual Skills', position: { x: 0, y: 0 } },
      ],
      rightItems: [
        { id: 'right-1', text: 'Senior Management Roles', position: { x: 0, y: 0 } },
        { id: 'right-2', text: 'Entry-Level Positions', position: { x: 0, y: 0 } },
        { id: 'right-3', text: 'Academic Excellence', position: { x: 0, y: 0 } },
        { id: 'right-4', text: 'International Business', position: { x: 0, y: 0 } },
      ],
      correctPairs: [
        { leftId: 'left-1', rightId: 'right-1' },
        { leftId: 'left-2', rightId: 'right-2' },
        { leftId: 'left-3', rightId: 'right-3' },
        { leftId: 'left-4', rightId: 'right-4' },
      ],
      difficulty: 'hard',
      category: 'Education & Experience',
    },
  ];

  useEffect(() => {
    if (open && !currentExercise) {
      setCurrentExercise(sampleExercises[0]);
      setTotalQuestions(sampleExercises[0].correctPairs.length);
    }
  }, [open, currentExercise]);

  const generateNewExercise = async () => {
    setIsGenerating(true);
    
    // Simulate AI generation delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // For now, cycle through sample exercises
    const currentIndex = sampleExercises.findIndex(ex => ex.id === currentExercise?.id);
    const nextIndex = (currentIndex + 1) % sampleExercises.length;
    const newExercise = { ...sampleExercises[nextIndex] };
    
    setCurrentExercise(newExercise);
    setTotalQuestions(newExercise.correctPairs.length);
    setShowResults(false);
    setScore(0);
    setConnections([]);
    setIsGenerating(false);
  };

  const handleDragStart = (itemId: string, event: React.MouseEvent | React.TouchEvent) => {
    if (showResults) return;
    
    // Prevent text selection
    event.preventDefault();
    event.stopPropagation();
    
    setIsDragging(true);
    setDraggedItem(itemId);
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
      const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
      
      setDragStart({
        x: clientX - rect.left,
        y: clientY - rect.top,
      });
      setDragCurrent({
        x: clientX - rect.left,
        y: clientY - rect.top,
      });
    }
  };

  const handleDragMove = (event: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !containerRef.current) return;
    
    // Prevent text selection
    event.preventDefault();
    
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    setDragCurrent({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });
  };

  const handleDragEnd = (targetItemId?: string) => {
    if (!isDragging || !draggedItem || !currentExercise) {
      setIsDragging(false);
      setDraggedItem(null);
      setDragStart(null);
      setDragCurrent(null);
      return;
    }
    
    // Check if this is a valid connection
    const isLeftItem = currentExercise.leftItems.some(item => item.id === draggedItem);
    const isRightItem = targetItemId && currentExercise.rightItems.some(item => item.id === targetItemId);
    
    if (isLeftItem && isRightItem && targetItemId) {
      // Create connection
      const connectionId = `conn-${draggedItem}-${targetItemId}`;
      const isCorrect = currentExercise.correctPairs.some(
        pair => pair.leftId === draggedItem && pair.rightId === targetItemId
      );
      
      // Remove any existing connections for these items
      const newConnections = connections.filter(
        conn => conn.from !== draggedItem && conn.to !== targetItemId
      );
      
      setConnections([
        ...newConnections,
        {
          id: connectionId,
          from: draggedItem,
          to: targetItemId,
          isCorrect,
        },
      ]);
    }
    
    setIsDragging(false);
    setDraggedItem(null);
    setDragStart(null);
    setDragCurrent(null);
  };

  const removeConnection = (connectionId: string) => {
    setConnections(connections.filter(conn => conn.id !== connectionId));
  };

  const checkAnswers = () => {
    if (!currentExercise) return;
    
    let correctCount = 0;
    const updatedConnections = connections.map(conn => {
      const isCorrect = currentExercise.correctPairs.some(
        pair => pair.leftId === conn.from && pair.rightId === conn.to
      );
      if (isCorrect) correctCount++;
      return { ...conn, isCorrect };
    });
    
    setConnections(updatedConnections);
    setScore(correctCount);
    setShowResults(true);
    setCompletedExercises(prev => [...prev, currentExercise]);
  };

  const resetExercise = () => {
    setConnections([]);
    setShowResults(false);
    setScore(0);
    setDraggedItem(null);
    setHoveredItem(null);
    setIsDragging(false);
    setDragStart(null);
    setDragCurrent(null);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '🟢';
      case 'medium': return '🟡';
      case 'hard': return '🔴';
      default: return '⚪';
    }
  };

  const renderConnection = (connection: any) => {
    if (!currentExercise || !containerDimensions) return null;
    
    const fromItem = [...currentExercise.leftItems, ...currentExercise.rightItems]
      .find(item => item.id === connection.from);
    const toItem = [...currentExercise.leftItems, ...currentExercise.rightItems]
      .find(item => item.id === connection.to);
    
    if (!fromItem || !toItem) return null;
    
    // Calculate actual positions based on item indices
    const fromIndex = currentExercise.leftItems.findIndex(item => item.id === connection.from);
    const toIndex = currentExercise.rightItems.findIndex(item => item.id === connection.to);
    
    if (fromIndex === -1 || toIndex === -1) return null;
    
    // Get actual container dimensions
    const containerWidth = containerDimensions.width;
    const containerHeight = containerDimensions.height;
    
    // Calculate positions based on the actual layout structure
    const containerPadding = isMobile ? 10 : 20;
    const leftColumnWidth = isMobile ? 140 : 160;
    const rightColumnWidth = isMobile ? 140 : 160;
    const itemSpacing = isMobile ? 100 : 120;
    const itemHeight = isMobile ? 80 : 100;
    const headerHeight = 40;
    
    // Left side positions - right edge of left column items
    const leftStartX = containerPadding + leftColumnWidth - 5;
    const leftStartY = containerPadding + headerHeight + 20 + (fromIndex * itemSpacing) + (itemHeight / 2);
    
    // Right side positions - left edge of right column items
    // Right column is positioned with right: 20px, so we need to calculate from the right edge
    const rightMargin = isMobile ? 10 : 20;
    const rightColumnLeftEdge = containerWidth - rightMargin - rightColumnWidth;
    const rightEndX = rightColumnLeftEdge + 5; // Left edge of right item
    const rightEndY = containerPadding + headerHeight + 20 + (toIndex * itemSpacing) + (itemHeight / 2);
    
    // Create a smooth curve between the points
    const controlX1 = leftStartX + (rightEndX - leftStartX) * 0.3;
    const controlY1 = leftStartY;
    const controlX2 = leftStartX + (rightEndX - leftStartX) * 0.7;
    const controlY2 = rightEndY;
    
    const pathData = `M ${leftStartX} ${leftStartY} C ${controlX1} ${controlY1} ${controlX2} ${controlY2} ${rightEndX} ${rightEndY}`;
    
    return (
      <motion.g
        key={`${connection.id}-${containerDimensions.width}-${containerDimensions.height}-${forceUpdate}`}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.3 }}
      >
        <path
          d={pathData}
          stroke={showResults 
            ? connection.isCorrect 
              ? '#10b981' 
              : '#ef4444'
            : '#8b5cf6'
          }
          strokeWidth={showResults ? 3 : 2}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={showResults ? 'none' : '5,5'}
          style={{
            filter: showResults 
              ? connection.isCorrect 
                ? 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.5))'
                : 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.5))'
              : 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))'
          }}
        />
        {showResults && (
          <circle
            cx={rightEndX}
            cy={rightEndY}
            r="6"
            fill={connection.isCorrect ? '#10b981' : '#ef4444'}
            style={{
              filter: connection.isCorrect 
                ? 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.8))'
                : 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.8))'
            }}
          />
        )}
      </motion.g>
    );
  };

  const renderDragLine = () => {
    if (!isDragging || !dragStart || !dragCurrent) return null;
    
    const startX = dragStart.x;
    const startY = dragStart.y;
    const endX = dragCurrent.x;
    const endY = dragCurrent.y;
    
    // Create a smooth curve for the drag line
    const controlX1 = startX + (endX - startX) * 0.3;
    const controlY1 = startY;
    const controlX2 = startX + (endX - startX) * 0.7;
    const controlY2 = endY;
    
    const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1} ${controlX2} ${controlY2} ${endX} ${endY}`;
    
    return (
      <path
        d={pathData}
        stroke="#8b5cf6"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="8,4"
        style={{
          filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.6))',
          animation: 'dash 1s linear infinite',
        }}
      />
    );
  };

  const renderMatchingItem = (item: any, isLeft: boolean) => {
    const isDragged = draggedItem === item.id;
    const isHovered = hoveredItem === item.id;
    const hasConnection = connections.some(conn => 
      conn.from === item.id || conn.to === item.id
    );
    
    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: 1, 
          y: 0,
          scale: isDragged ? 1.05 : 1,
        }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'relative',
          zIndex: isDragged ? 1000 : 10,
          width: '100%',
        }}
        onMouseDown={(e) => handleDragStart(item.id, e)}
        onTouchStart={(e) => handleDragStart(item.id, e)}
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(null)}
        onMouseUp={(e) => {
          if (isDragging && draggedItem && draggedItem !== item.id) {
            handleDragEnd(item.id);
          }
        }}
        onTouchEnd={(e) => {
          if (isDragging && draggedItem && draggedItem !== item.id) {
            handleDragEnd(item.id);
          }
        }}
      >
        <Card
          sx={{
            width: isMobile ? 120 : 150,
            minHeight: isMobile ? 60 : 80,
            cursor: showResults ? 'default' : 'grab',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            background: hasConnection 
              ? showResults
                ? connections.find(conn => conn.from === item.id || conn.to === item.id)?.isCorrect
                  ? alpha('#10b981', 0.1)
                  : alpha('#ef4444', 0.1)
                : alpha('#8b5cf6', 0.1)
              : (isDragging && draggedItem && draggedItem !== item.id && currentExercise &&
                 ((currentExercise.leftItems.some(leftItem => leftItem.id === draggedItem) && 
                   currentExercise.rightItems.some(rightItem => rightItem.id === item.id)) ||
                  (currentExercise.rightItems.some(rightItem => rightItem.id === draggedItem) && 
                   currentExercise.leftItems.some(leftItem => leftItem.id === item.id))))
              ? alpha('#8b5cf6', 0.2)
              : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            border: hasConnection
              ? showResults
                ? connections.find(conn => conn.from === item.id || conn.to === item.id)?.isCorrect
                  ? '2px solid #10b981'
                  : '2px solid #ef4444'
                : '2px solid #8b5cf6'
              : (isDragging && draggedItem && draggedItem !== item.id && currentExercise &&
                 ((currentExercise.leftItems.some(leftItem => leftItem.id === draggedItem) && 
                   currentExercise.rightItems.some(rightItem => rightItem.id === item.id)) ||
                  (currentExercise.rightItems.some(rightItem => rightItem.id === draggedItem) && 
                   currentExercise.leftItems.some(leftItem => leftItem.id === item.id))))
              ? '2px solid #8b5cf6'
              : '2px solid transparent',
            backgroundImage: hasConnection
              ? 'none'
              : 'linear-gradient(white, white), linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: isDragged
              ? '0 20px 60px rgba(139, 92, 246, 0.4)'
              : isHovered
              ? '0 8px 30px rgba(139, 92, 246, 0.2)'
              : '0 4px 20px rgba(139, 92, 246, 0.1)',
            transition: 'all 0.3s ease',
            transform: isDragged ? 'rotate(2deg)' : 'rotate(0deg)',
            '&:hover': {
              transform: isDragged ? 'rotate(2deg) scale(1.05)' : 'scale(1.02)',
            },
          }}
        >
          <CardContent sx={{ p: isMobile ? 1.5 : 2, textAlign: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 1,
              mb: 1,
            }}>
              <LinkIcon sx={{ 
                fontSize: isMobile ? 16 : 20, 
                color: hasConnection ? (showResults ? 
                  connections.find(conn => conn.from === item.id || conn.to === item.id)?.isCorrect
                    ? '#10b981' : '#ef4444'
                  : '#8b5cf6'
                ) : '#6b7280',
              }} />
              {hasConnection && showResults && (
                <Box sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: connections.find(conn => conn.from === item.id || conn.to === item.id)?.isCorrect
                    ? '#10b981' : '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {connections.find(conn => conn.from === item.id || conn.to === item.id)?.isCorrect
                    ? <CheckIcon sx={{ fontSize: 12, color: 'white' }} />
                    : <ErrorIcon sx={{ fontSize: 12, color: 'white' }} />
                  }
                </Box>
              )}
            </Box>
            <Typography sx={{ 
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              fontWeight: 600,
              color: hasConnection 
                ? showResults
                  ? connections.find(conn => conn.from === item.id || conn.to === item.id)?.isCorrect
                    ? '#10b981' : '#ef4444'
                  : '#8b5cf6'
                : '#1f2937',
              lineHeight: 1.3,
              textAlign: 'center',
            }}>
              {item.text}
            </Typography>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMaximized ? false : (isMobile ? "sm" : "lg")}
      fullWidth={isMaximized || isMobile}
      fullScreen={isMaximized || isMobile}
      PaperProps={{
        sx: {
          borderRadius: (isMaximized || isMobile) ? 0 : 4,
          background: 'white',
          border: (isMaximized || isMobile) ? 'none' : '3px solid transparent',
          backgroundImage: (isMaximized || isMobile) ? 'none' : 'linear-gradient(white, white), linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          boxShadow: (isMaximized || isMobile) ? 'none' : '0 20px 60px rgba(236, 72, 153, 0.3), 0 8px 25px rgba(139, 92, 246, 0.2)',
          overflow: 'hidden',
          minHeight: (isMaximized || isMobile) ? '100vh' : '600px',
          maxHeight: (isMaximized || isMobile) ? '100vh' : '90vh',
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        py: isMobile ? 1.5 : 2,
        px: isMobile ? 2 : 4,
        background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
        color: 'white',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? 1 : 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          pointerEvents: 'none',
        }
      }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
            position: 'relative',
            zIndex: 2,
            minWidth: isMobile ? 40 : 48,
            minHeight: isMobile ? 40 : 48,
          }}
        >
          <CloseIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
        </IconButton>

        {/* Center Content */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: isMobile ? 1 : 2,
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minWidth: 0,
        }}>
          <Box sx={{
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            flexShrink: 0,
          }}>
            <TimelineIcon sx={{ fontSize: isMobile ? 18 : 22, color: 'white' }} />
          </Box>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              fontWeight: 700, 
              fontSize: isMobile ? '1.1rem' : '1.5rem',
              textAlign: isMobile ? 'center' : 'left',
              lineHeight: 1.2,
            }}
          >
            {isMobile ? 'Matching Exercise' : 'Matching Exercise'}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: isMobile ? 0.5 : 1,
          flexShrink: 0,
        }}>
          {/* Maximize/Minimize Button - Hide on mobile since it's already fullscreen */}
          {!isMobile && (
            <IconButton
              onClick={() => setIsMaximized(!isMaximized)}
              sx={{
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
                position: 'relative',
                zIndex: 2,
                minWidth: isMobile ? 36 : 40,
                minHeight: isMobile ? 36 : 40,
              }}
              title={isMaximized ? 'Minimize' : 'Maximize'}
            >
              {isMaximized ? <MinimizeIcon sx={{ fontSize: isMobile ? 18 : 20 }} /> : <MaximizeIcon sx={{ fontSize: isMobile ? 18 : 20 }} />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ 
        px: isMobile ? 2 : 4, 
        py: isMobile ? 2 : 4,
        background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
        position: 'relative',
        overflow: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(236, 72, 153, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        {currentExercise && (
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Exercise Info */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? 1 : 2, 
              mb: isMobile ? 3 : 4,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}>
              <Chip
                label={`${getDifficultyIcon(currentExercise.difficulty)} ${currentExercise.difficulty.toUpperCase()}`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha(getDifficultyColor(currentExercise.difficulty), 0.1),
                  color: getDifficultyColor(currentExercise.difficulty),
                  fontWeight: 600,
                  border: `1px solid ${alpha(getDifficultyColor(currentExercise.difficulty), 0.3)}`,
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
              <Chip
                label={currentExercise.category}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha('#3b82f6', 0.1),
                  color: '#3b82f6',
                  fontWeight: 600,
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
              <Chip
                label={`${currentExercise.correctPairs.length} Pairs`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha('#8b5cf6', 0.1),
                  color: '#8b5cf6',
                  fontWeight: 600,
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
            </Box>

            {/* Exercise Title & Description */}
            <Paper
              elevation={2}
              sx={{
                p: isMobile ? 2 : 3,
                mb: isMobile ? 3 : 4,
                borderRadius: 3,
                background: 'white',
                border: '2px solid rgba(236, 72, 153, 0.1)',
              }}
            >
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: '#1f2937',
                mb: 1,
                fontSize: isMobile ? '1.2rem' : '1.5rem',
              }}>
                {currentExercise.title}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: '#6b7280',
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
              }}>
                {currentExercise.description}
              </Typography>
            </Paper>

            {/* Matching Area */}
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 3,
                mb: isMobile ? 3 : 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '2px solid rgba(139, 92, 246, 0.1)',
                position: 'relative',
                minHeight: isMobile ? '500px' : '600px',
                height: isMobile ? '500px' : '600px',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                userSelect: isDragging ? 'none' : 'auto',
                WebkitUserSelect: isDragging ? 'none' : 'auto',
                MozUserSelect: isDragging ? 'none' : 'auto',
                msUserSelect: isDragging ? 'none' : 'auto',
              }}
              ref={containerRef}
              onMouseMove={handleDragMove}
              onTouchMove={handleDragMove}
              onMouseUp={() => handleDragEnd()}
              onTouchEnd={() => handleDragEnd()}
              onMouseLeave={() => handleDragEnd()}
            >
              {/* SVG for connections */}
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              >
                <defs>
                  <style>
                    {`
                      @keyframes dash {
                        to {
                          stroke-dashoffset: -20;
                        }
                      }
                    `}
                  </style>
                </defs>
                {connections.map(renderConnection)}
                {renderDragLine()}
              </svg>

              {/* Left Column */}
              <Box sx={{
                position: 'absolute',
                left: isMobile ? 10 : 20,
                top: isMobile ? 10 : 20,
                width: isMobile ? 140 : 160,
                zIndex: 10,
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  color: '#1f2937',
                  mb: 2,
                  textAlign: 'center',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                }}>
                  Connect From
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentExercise.leftItems.map(item => renderMatchingItem(item, true))}
                </Box>
              </Box>

              {/* Right Column */}
              <Box sx={{
                position: 'absolute',
                right: isMobile ? 10 : 20,
                top: isMobile ? 10 : 20,
                width: isMobile ? 140 : 160,
                zIndex: 10,
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  color: '#1f2937',
                  mb: 2,
                  textAlign: 'center',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                }}>
                  Connect To
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentExercise.rightItems.map(item => renderMatchingItem(item, false))}
                </Box>
              </Box>

            </Paper>

            {/* Progress Bar */}
            {!showResults && (
              <Box sx={{ mb: isMobile ? 3 : 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Progress
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {connections.length} / {totalQuestions}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(connections.length / totalQuestions) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: alpha('#e2e8f0', 0.3),
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            {/* Results */}
            {showResults && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: score === totalQuestions 
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.1) 100%)'
                      : score > totalQuestions / 2
                      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(248, 113, 113, 0.1) 100%)',
                    border: `2px solid ${score === totalQuestions 
                      ? 'rgba(16, 185, 129, 0.3)'
                      : score > totalQuestions / 2
                      ? 'rgba(245, 158, 11, 0.3)'
                      : 'rgba(239, 68, 68, 0.3)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                    {score === totalQuestions ? (
                      <CheckIcon sx={{ fontSize: 32, color: '#10b981' }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 32, color: score > totalQuestions / 2 ? '#f59e0b' : '#ef4444' }} />
                    )}
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: score === totalQuestions 
                        ? '#10b981'
                        : score > totalQuestions / 2
                        ? '#f59e0b'
                        : '#ef4444'
                    }}>
                      {score}/{totalQuestions}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ 
                    textAlign: 'center', 
                    fontWeight: 600,
                    color: score === totalQuestions 
                      ? '#10b981'
                      : score > totalQuestions / 2
                      ? '#f59e0b'
                      : '#ef4444'
                  }}>
                    {score === totalQuestions 
                      ? 'Perfect! 🎉' 
                      : score > totalQuestions / 2
                      ? 'Good Job! 👍'
                      : 'Keep Trying! 💪'}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#6b7280' }}>
                    {Math.round((score / totalQuestions) * 100)}% Correct
                  </Typography>
                </Paper>
              </motion.div>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ 
        p: isMobile ? 2 : 4, 
        pt: isMobile ? 1 : 2,
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderTop: '1px solid rgba(236, 72, 153, 0.1)',
        position: 'relative',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(236, 72, 153, 0.3) 50%, transparent 100%)',
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: isMobile ? 1 : 2, 
          width: '100%', 
          justifyContent: isMobile ? 'center' : 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2,
            flexDirection: isMobile ? 'row' : 'row',
            justifyContent: isMobile ? 'center' : 'flex-start',
          }}>
            <Button
              onClick={resetExercise}
              variant="outlined"
              disabled={!currentExercise || isGenerating}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                px: isMobile ? 2 : 3,
                py: isMobile ? 1 : 1.5,
                borderColor: '#e2e8f0',
                color: '#64748b',
                fontSize: isMobile ? '0.875rem' : '1rem',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Reset
            </Button>
            
            <Button
              onClick={generateNewExercise}
              variant="contained"
              disabled={isGenerating}
              size={isMobile ? 'small' : 'medium'}
              startIcon={isGenerating ? <CircularProgress size={isMobile ? 16 : 20} /> : <ShuffleIcon />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                px: isMobile ? 2 : 3,
                py: isMobile ? 1 : 1.5,
                fontSize: isMobile ? '0.875rem' : '1rem',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
                },
                '&:disabled': {
                  backgroundColor: '#e2e8f0',
                  color: '#94a3b8',
                },
              }}
            >
              {isGenerating ? (isMobile ? 'Generating...' : 'Generating...') : (isMobile ? 'New Exercise' : 'Generate New Exercise')}
            </Button>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2,
            justifyContent: isMobile ? 'center' : 'flex-end',
          }}>
            {!showResults ? (
              <Button
                onClick={checkAnswers}
                variant="contained"
                disabled={!currentExercise || connections.length === 0}
                size={isMobile ? 'small' : 'medium'}
                startIcon={<CheckIcon />}
                sx={{
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: isMobile ? 3 : 4,
                  py: isMobile ? 1 : 1.5,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  },
                  '&:disabled': {
                    backgroundColor: '#e2e8f0',
                    color: '#94a3b8',
                  },
                }}
              >
                {isMobile ? 'Check' : 'Check Answers'}
              </Button>
            ) : (
              <Button
                onClick={onClose}
                variant="contained"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: isMobile ? 3 : 4,
                  py: isMobile ? 1 : 1.5,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                  },
                }}
              >
                Close
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MatchingExerciseModal;
